import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useHistory } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  ListItemButton,
  Box,
  Typography,
  ButtonGroup,
  Button,
  Tooltip,
} from "@mui/material";
import { resolveContactAvatar } from "../../utils/avatar";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import CustomChip from "../ui/CustomChip";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { Done, HighlightOff, SwapHoriz } from "@material-ui/icons";
import {
  MessageSquare as LucideMessageSquare,
  Contact as LucideContact,
} from "lucide-react";
import {
  parseMediaString,
  getMediaTypeInfo,
} from "../../helpers/mediaTypeHelper";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    height: "60px",
    padding: "8px 12px",
    borderRadius: `${theme.borderRadius.medium} !important`,
    border: `1px solid ${theme.palette.divider} !important`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}15 !important`,
    },
    "&.Mui-selected": {
      backgroundColor: `${theme.palette.primary.main}15 !important`,
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}15 !important`,
      },
    },
    ".MuiPopover-root &": {
      padding: theme.spacing(0.5, 0.75),
      borderRadius: theme.shape.borderRadius,
      height: "55px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "6px 8px",
      ".MuiPopover-root &": {
        padding: theme.spacing(0.5, 0.5),
      },
    },
  },

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginLeft: "8px",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "6px",
    },
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },

  contactName: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
    fontSize: "0.9rem !important",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "70%",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
      maxWidth: "60%",
    },
  },

  lastMessageTime: {
    fontSize: "0.9rem !important",
    color: theme.palette.text.secondary,
    marginLeft: "auto",
    minWidth: "40px",
    textAlign: "right",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem !important",
      minWidth: "35px",
    },
  },

  lastMessageTimeUnread: {
    fontSize: "0.9rem !important",
    color: theme.palette.success.main,
    fontWeight: "bold",
    marginLeft: "auto",
    minWidth: "40px",
    textAlign: "right",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem !important",
      minWidth: "35px",
    },
  },

  connectionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "4px",
    width: "100%",
  },

  ticketInfo: {
    flex: 1,
    minWidth: 0,
    marginLeft: "8px",
  },

  ticketHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  connectionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginTop: 4,
  },

  connectionName: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },

  notificationItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5, 1),
    },
  },

  button: {
    minWidth: "18px",
    height: "18px",
    color: theme.palette.action.active,
    "&:hover": {
      backgroundColor: "rgba(189, 189, 189, 0.5) !important",
    },
  },

  iconSize: {
    fontSize: "1rem",
    color: theme.palette.action.active,
  },

  buttonGroup: {
    "& .MuiButton-text": {
      borderColor: theme.palette.action.active,
      borderRadius: "10px",
      "&:hover": {
        borderColor: theme.palette.action.hover,
      },
    },
  },

  buttonsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "3px",
  },
}));

const TicketListItem = ({
  ticket,
  setTabOpen,
  setModalOpen,
  hideLastMessage = false,
}) => {
  const classes = useStyles();

  const history = useHistory();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { get: getSetting } = useCompanySettings();
  const [, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSelectTicket = useCallback(
    (ticket) => {
      if (!isMounted.current) return;

      const code = uuidv4();
      const { id, uuid } = ticket;
      setCurrentTicket({ id, uuid, code });

      setTimeout(() => {
        if (isMounted.current) {
          history.push(`/tickets/${ticket.uuid}`);
        }
      }, 10);
    },
    [history, setCurrentTicket]
  );

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      if (!isMounted.current) return;

      if (setModalOpen) setModalOpen(true);

      setAcceptTicketWithouSelectQueueOpen(true);
    },
    [setModalOpen]
  );

  const handleOpenTransferModal = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (!isMounted.current) return;
    setLoading(true);

    if (setModalOpen) setModalOpen(true);

    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
  };

  const handleCloseAlert = useCallback(() => {
    if (isMounted.current) {
      setOpenAlert(false);
      setLoading(false);
    }
  }, [setLoading]);

  const handleCloseIgnoreTicket = async (id) => {
    if (!isMounted.current) return;
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
    if (isMounted.current) {
      setLoading(false);
    }
  };

  const handleSendMessage = async (id) => {
    if (!isMounted.current) return;
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      if (isMounted.current) {
        toastError(err);
      }
    }

    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      if (isMounted.current) {
        toastError(err);
      }
    }
  };

  const handleAcepptTicket = async (id) => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const desiredStatus =
        ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open";
      const otherTicket = await api.put(`/tickets/${id}`, {
        status: desiredStatus,
        userId: user?.id,
      });

      if (!isMounted.current) return;

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);

          if (isMounted.current) {
            if (setTabOpen) {
              setTabOpen(desiredStatus);
            }
            history.push(`/tickets/${otherTicket.data.uuid}`);
          }
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });

          if (!isMounted.current) return;

          if (
            setting.sendGreetingAccepted === "enabled" &&
            (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
          ) {
            await handleSendMessage(ticket.id);
          }
        } catch (err) {
          if (isMounted.current) {
            toastError(err);
          }
        }

        if (isMounted.current) {
          setLoading(false);

          if (setTabOpen) {
            setTabOpen(desiredStatus);
          }
          history.push(`/tickets/${ticket.uuid}`);
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  const truncate = (str, len) => {
    if (str && str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const getConnectionProps = useMemo(() => {
    if (ticket.channel === "whatsapp") {
      return {
        label: truncate(ticket.whatsapp?.name || "WhatsApp", 20),
        backgroundColor: "rgba(37, 211, 102, 0.1)",
        textColor: "#25D366",
        borderColor: "#25D366",
      };
    }
    if (ticket.channel === "instagram") {
      return {
        label: truncate(ticket.whatsapp?.name || "Instagram", 20),
        backgroundColor: "rgba(225, 48, 108, 0.1)",
        textColor: "#e1306c",
        borderColor: "#e1306c",
      };
    }
    if (ticket.channel === "facebook") {
      return {
        label: truncate(ticket.whatsapp?.name || "Facebook", 20),
        backgroundColor: "rgba(59, 89, 152, 0.1)",
        textColor: "#3b5998",
        borderColor: "#3b5998",
      };
    }
    return {
      label: truncate(
        ticket.whatsapp?.name || ticket.channel?.toUpperCase() || "Conexão",
        20
      ),
      backgroundColor: "rgba(125, 125, 125, 0.1)",
      textColor: "#7D7D7D",
      borderColor: "#7D7D7D",
    };
  }, [ticket.channel, ticket.whatsapp]);

  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const normalizeMediaPreviewText = useCallback((value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    if (
      /^\[(image|imagem|video|vídeo|audio|arquivo|document|documento|sticker)\]$/i.test(
        text
      )
    ) {
      return "";
    }
    if (
      /^\d+\.(mp3|ogg|wav|m4a|opus|webm|jpg|jpeg|png|webp|mp4|pdf|doc|docx)$/i.test(
        text
      )
    ) {
      return "";
    }
    return text;
  }, []);

  const normalizedFromMe = useMemo(() => {
    const hasLastMessageFromMe =
      ticket?.lastMessageFromMe !== undefined &&
      ticket?.lastMessageFromMe !== null;
    const value = hasLastMessageFromMe
      ? ticket?.lastMessageFromMe
      : ticket?.fromMe;

    if (value !== undefined && value !== null) {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") {
        if (value === 1) return true;
        if (value === 0) return false;
      }
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "t", "yes", "y"].includes(normalized)) return true;
        if (["false", "0", "f", "no", "n"].includes(normalized)) return false;
      }
    }

    if (Array.isArray(ticket?.messages) && ticket.messages.length > 0) {
      for (let i = ticket.messages.length - 1; i >= 0; i--) {
        const msg = ticket.messages[i];
        if (msg && typeof msg.fromMe === "boolean") {
          return msg.fromMe;
        }
      }
    }

    return null;
  }, [ticket?.lastMessageFromMe, ticket?.fromMe, ticket?.messages]);

  const lastMessageContent = useMemo(() => {
    if (!ticket) return "Mensagem";
    if (typeof ticket.lastMessage === "string" && ticket.lastMessage.trim()) {
      return ticket.lastMessage.trim();
    }
    if (typeof ticket.lastMessageText === "string" && ticket.lastMessageText.trim()) {
      return ticket.lastMessageText.trim();
    }
    if (typeof ticket.lastMessageBody === "string" && ticket.lastMessageBody.trim()) {
      return ticket.lastMessageBody.trim();
    }
    if (typeof ticket.body === "string" && ticket.body.trim()) {
      return ticket.body.trim();
    }
    if (ticket.lastMessageObj) {
      if (typeof ticket.lastMessageObj === "string" && ticket.lastMessageObj.trim()) {
        return ticket.lastMessageObj.trim();
      }
      if (typeof ticket.lastMessageObj.body === "string" && ticket.lastMessageObj.body.trim()) {
        return ticket.lastMessageObj.body.trim();
      }
      if (typeof ticket.lastMessageObj.message === "string" && ticket.lastMessageObj.message.trim()) {
        return ticket.lastMessageObj.message.trim();
      }
    }
    if (Array.isArray(ticket.messages) && ticket.messages.length > 0) {
      for (let i = ticket.messages.length - 1; i >= 0; i--) {
        const msg = ticket.messages[i];
        if (!msg) continue;
        if (typeof msg.body === "string" && msg.body.trim()) {
          return msg.body.trim();
        }
        if (typeof msg.message === "string" && msg.message.trim()) {
          return msg.message.trim();
        }
        if (msg.mediaType || msg.mediaUrl) {
          return `media:${msg.mediaType || "image"}|${msg.mediaUrl || ""}|${msg.body || ""}`;
        }
      }
    }
    return "Mensagem";
  }, [ticket]);

  const cleanTemplateText = useCallback((text) => {
    if (typeof text !== "string") return "";
    let clean = text.trim();
    if (/^\[template\]\s*/i.test(clean)) {
      clean = clean.replace(/^\[template\]\s*/i, "").trim();
    } else if (/^template:\s*/i.test(clean)) {
      clean = clean.replace(/^template:\s*/i, "").trim();
    } else if (/^\[hsm\]\s*/i.test(clean)) {
      clean = clean.replace(/^\[hsm\]\s*/i, "").trim();
    }
    return clean;
  }, []);

  const getLastMessagePlainText = useCallback(
    (msg) => {
      if (!msg) return "";

      const senderName =
        normalizedFromMe === true
          ? ticket.user?.name || "Você"
          : ticket.contact?.name || "Cliente";

      const prefix = senderName ? `${senderName}: ` : "";

      if (msg === "Contato" || msg.startsWith("Contato:")) {
        const contactName =
          msg === "Contato" ? "" : msg.replace("Contato:", "").trim();
        return `${prefix}Contato${contactName ? `: ${contactName}` : ""}`;
      }

      const mediaInfo = parseMediaString(msg);
      if (mediaInfo) {
        const cleanCaption = normalizeMediaPreviewText(mediaInfo.caption);
        const textToShow = cleanCaption || mediaInfo.label || "Mídia";
        return `${prefix}${textToShow}`;
      }

      if (msg.startsWith("media:")) {
        const payload = msg.slice(6);
        const [type, ...rest] = payload.split("|");
        const caption = rest.join("|") || "";

        const typeInfo = getMediaTypeInfo(type);
        const cleanCaption = normalizeMediaPreviewText(caption);
        const textToShow = cleanCaption || typeInfo.label || "Mídia";
        return `${prefix}${textToShow}`;
      }

      const bracketMatch =
        typeof msg === "string" &&
        msg.match(
          /^\[(image|imagem|video|vídeo|audio|arquivo|document|documento|sticker)\]$/i
        );
      if (bracketMatch) {
        const mediaType = bracketMatch[1].toLowerCase();
        const typeInfo = getMediaTypeInfo(mediaType);
        return `${prefix}${typeInfo.label || "Mídia"}`;
      }

      const cleanText = cleanTemplateText(msg);
      return `${prefix}${cleanText}`;
    },
    [
      normalizedFromMe,
      ticket.user?.name,
      ticket.contact?.name,
      normalizeMediaPreviewText,
      cleanTemplateText,
    ]
  );

  const renderLastMessage = useCallback(
    (msg) => {
      if (!msg) return null;

      const senderName =
        normalizedFromMe === true
          ? ticket.user?.name || "Você"
          : ticket.contact?.name || "Cliente";

      const senderBg = "#0ea5e9";
      const senderColor = "#ffffff";
      const bodyBg = "#8d6e63";
      const bodyColor = "#ffffff";

      const renderWithSender = (IconComponent, contentText) => (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: "6px",
            overflow: "hidden",
            maxWidth: "100%",
            fontSize: "0.75rem",
            lineHeight: 1.35,
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.12)",
            marginTop: "3px",
            marginBottom: "3px",
          }}
        >
          <span
            style={{
              backgroundColor: senderBg,
              color: senderColor,
              padding: "3px 8px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
              borderRight: "1px solid rgba(255, 255, 255, 0.25)",
            }}
          >
            {IconComponent && <IconComponent size={13} style={{ flexShrink: 0 }} />}
            <span>{senderName}:</span>
          </span>
          <span
            style={{
              backgroundColor: bodyBg,
              color: bodyColor,
              padding: "3px 8px",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>{contentText}</span>
          </span>
        </div>
      );

      if (msg === "Contato" || msg.startsWith("Contato:")) {
        const contactName =
          msg === "Contato" ? "" : msg.replace("Contato:", "").trim();
        const content = `Contato${contactName ? `: ${contactName}` : ""}`;
        return renderWithSender(LucideContact, content);
      }

      const mediaInfo = parseMediaString(msg);
      if (mediaInfo) {
        const { icon: Icon, label, caption } = mediaInfo;
        const cleanCaption = normalizeMediaPreviewText(caption);
        const textToShow = cleanCaption || label || "Mídia";
        return renderWithSender(Icon, textToShow);
      }

      if (msg.startsWith("media:")) {
        const payload = msg.slice(6);
        const [type, ...rest] = payload.split("|");
        const caption = rest.join("|") || "";

        const typeInfo = getMediaTypeInfo(type);
        const { icon: Icon, label } = typeInfo;
        const cleanCaption = normalizeMediaPreviewText(caption);
        const textToShow = cleanCaption || label || "Mídia";
        return renderWithSender(Icon, textToShow);
      }

      const bracketMatch =
        typeof msg === "string" &&
        msg.match(
          /^\[(image|imagem|video|vídeo|audio|arquivo|document|documento|sticker)\]$/i
        );
      if (bracketMatch) {
        const mediaType = bracketMatch[1].toLowerCase();
        const typeInfo = getMediaTypeInfo(mediaType);
        const { icon: Icon, label } = typeInfo;
        return renderWithSender(Icon, label || "Mídia");
      }

      const cleanText = cleanTemplateText(msg);
      return renderWithSender(LucideMessageSquare, cleanText);
    },
    [
      normalizedFromMe,
      ticket.user?.name,
      ticket.contact?.name,
      normalizeMediaPreviewText,
      cleanTemplateText,
    ]
  );

  return (
    <>
      {transferTicketModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1500,
            backgroundColor: "transparent",
          }}
          onClick={stopPropagation}
        />
      )}
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            if (isMounted.current) {
              if (setModalOpen) setModalOpen(false);
              setAcceptTicketWithouSelectQueueOpen(false);
            }
          }}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            if (isMounted.current) {
              if (setModalOpen) setModalOpen(false);
              setTransferTicketModalOpen(false);
            }
          }}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}

      <ListItemButton
        dense
        onClick={() => handleSelectTicket(ticket)}
        className={classes.ticket}
        style={{ height: "60px" }}
      >
        <Avatar
          style={{
            width: 32,
            height: 32,
          }}
          src={resolveContactAvatar(ticket?.contact)}
          alt={ticket?.contact?.name}
        />

        <Box className={classes.ticketInfo}>
          <Box className={classes.ticketHeader}>
            <Typography className={classes.contactName} component="span">
              {truncate(ticket.contact?.name, 22)}
            </Typography>

            <Typography
              className={
                Number(ticket.unreadMessages) > 0
                  ? classes.lastMessageTimeUnread
                  : classes.lastMessageTime
              }
              component="span"
            >
              {isSameDay(parseISO(ticket.updatedAt), new Date())
                ? format(parseISO(ticket.updatedAt), "HH:mm")
                : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
            </Typography>
          </Box>

          <Box className={classes.connectionContainer}>
            {!hideLastMessage && (
              <Tooltip
                title={getLastMessagePlainText(lastMessageContent) || ""}
              >
                <div style={{ display: "inline-flex", alignItems: "center", margin: "2px 0" }}>
                  {renderLastMessage(lastMessageContent)}
                </div>
              </Tooltip>
            )}
            <CustomChip
              label={getConnectionProps.label}
              size="small"
              icon={
                <ConnectionIcon
                  connectionType={ticket.channel}
                  style={{ fontSize: "0.65rem" }}
                />
              }
              backgroundColor={getConnectionProps.backgroundColor}
              textColor={getConnectionProps.textColor}
              borderColor={getConnectionProps.borderColor}
              withTooltip
              tooltipTitle={ticket.whatsapp?.name || "Conexão"}
              customStyles={{
                height: "16px",
                padding: "1px 4px",
                fontSize: "0.65rem",
              }}
            />

            {ticket.status === "pending" && (
              <Box className={classes.buttonsContainer}>
                <ButtonGroup
                  className={classes.buttonGroup}
                  variant="text"
                  color="primary"
                  aria-label="text primary button group"
                >
                  {ticket.status === "pending" &&
                    (ticket.queueId === null ||
                      ticket.queueId === undefined) && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.accept")}`}
                      >
                        <Button
                          className={classes.button}
                          aria-label="accept"
                          onClick={(e) =>
                            handleOpenAcceptTicketWithouSelectQueue(e)
                          }
                        >
                          <Done className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}
                  {ticket.status === "pending" && ticket.queueId !== null && (
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Button
                        className={classes.button}
                        aria-label="accept"
                        onClick={(e) => handleAcepptTicket(ticket.id)}
                      >
                        <Done className={classes.iconSize} />
                      </Button>
                    </Tooltip>
                  )}

                  {!ticket.isGroup &&
                    (ticket.status === "pending" ||
                      ticket.status === "open") && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.transfer")}`}
                      >
                        <Button
                          className={classes.button}
                          aria-label="transfer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenTransferModal(e);
                          }}
                        >
                          <SwapHoriz className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}

                  {ticket.status === "pending" &&
                    (user.userClosePendingTicket === "enabled" ||
                      user.profile === "admin") && (
                      <Tooltip
                        title={`${i18n.t("ticketsList.buttons.ignore")}`}
                      >
                        <Button
                          className={classes.button}
                          aria-label="ignore"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseIgnoreTicket(ticket.id);
                          }}
                        >
                          <HighlightOff className={classes.iconSize} />
                        </Button>
                      </Tooltip>
                    )}
                </ButtonGroup>
              </Box>
            )}
          </Box>
        </Box>
      </ListItemButton>
    </>
  );
};

export default React.memo(TicketListItem, (prevProps, nextProps) => {
  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.status === nextProps.ticket.status &&
    prevProps.ticket.unreadMessages === nextProps.ticket.unreadMessages &&
    prevProps.ticket.lastMessage === nextProps.ticket.lastMessage &&
    prevProps.ticket.updatedAt === nextProps.ticket.updatedAt &&
    prevProps.hideLastMessage === nextProps.hideLastMessage
  );
});
